import { BodyShort01 } from 'module/ui/ozone/atoms';
import { Link } from '@rocketlane/rocket-router';
import { DrawerProps } from 'antd';
import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { CloseIcon, Drawer as DrawerAtom } from './drawer';
import { Heading03, HelperText02, Short01 } from '../../styles';
import { Breadcrumb } from '../breadcrumb';

export const CustomLayoutDrawer = styled(DrawerAtom)`
  .ant-drawer-header {
    /* display: none; */
    padding: 0;
  }

  .ant-drawer-close {
    display: flex;
    position: absolute;
    top: var(--drawer-spacing-around);
    right: var(--drawer-spacing-around);
  }
  .ant-drawer-body {
    padding: 0;
  }
`;

const Divider = styled.div`
  border-top: 1px solid var(--border-color);
`;

const Separator = styled.div`
  margin: 0 var(--spacing-02);

  &::after {
    content: '·';
  }
`;

const TitleDescription = styled.div`
  ${Short01};
  color: var(--gray-50);
`;
const Title = styled.div`
  ${Heading03};
  ${Breadcrumb} {
    ${Short01};
    color: var(--text-tertiary);
    li:last-child {
      color: var(--text-tertiary);
    }
  }
`;

interface DrawerTitleProps {
  title?: ReactNode;
  description?: ReactNode;
  breadcrumbs?: ReactNode;
  className?: string;
}
const TitleWithDescription: FC<React.PropsWithChildren<DrawerTitleProps>> = ({
  title,
  description,
  children,
  className,
  breadcrumbs,
  ...props
}) => {
  return (
    <Title className={className} {...props}>
      {breadcrumbs != null ? (
        <Breadcrumb separator="">{breadcrumbs}</Breadcrumb>
      ) : null}
      {children != null ? (
        children
      ) : (
        <>
          {breadcrumbs != null ? (
            <Breadcrumb separator="">{breadcrumbs}</Breadcrumb>
          ) : null}
          {title}
          {description != null ? (
            <TitleDescription>{description}</TitleDescription>
          ) : null}
        </>
      )}
    </Title>
  );
};
const StyledTitle = styled(TitleWithDescription)``;

type DrawerTitle = typeof StyledTitle & {
  Title: typeof Title;
  Description: typeof TitleDescription;
};
const DrawerTitle: DrawerTitle = StyledTitle as any;

DrawerTitle.Title = Title;
DrawerTitle.Description = TitleDescription;

const Content = styled.div`
  ${BodyShort01};
`;

const Footer = styled.div<{ $rightAlignedAction?: boolean }>`
  ${({ $rightAlignedAction }) =>
    $rightAlignedAction
      ? css`
          display: flex;
          align-items: center;
          gap: var(--spacing-03);
          justify-content: flex-end;
        `
      : css`
          display: flex;
          align-items: center;
          gap: var(--spacing-03);
          justify-content: center;
        `};
`;

const DrawerComponent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  height: 100%;

  ${Title} {
    padding: var(--spacing-05) var(--spacing-06);
    flex: 0 0 auto;
    border-bottom: 1px solid var(--divider-default);
  }

  ${Content} {
    padding: var(--spacing-05) var(--spacing-06) calc(2 * var(--spacing-05));
    flex: 1;
    overflow-y: auto;
  }

  ${Footer} {
    border-top: 1px solid var(--divider-subtle);
    flex: 0 0 auto;
    padding: var(--spacing-05) var(--spacing-06);
  }

  ${Divider} {
    margin-top: var(--spacing-05);
  }
`;

const HelpText = styled.div`
  ${HelperText02};
  padding: 8px 10px 10px 0;
  font-size: 14px;
  line-height: 1; /* Matches the font size exactly */

  a {
    margin-left: var(--spacing-02);
    font-size: 14px;
    padding: 8px 10px 10px 0;
    line-height: 1; /* Matches the font size exactly */
  }
`;

type CustomLayoutDrawerContent = typeof DrawerComponent & {
  Title: DrawerTitle;
  Content: typeof Content;
  Divider: typeof Divider;
  Separator: typeof Separator;
  Footer: typeof Footer;
  TitleHelpText: typeof HelpText;
};

const CustomLayoutDrawerContent: CustomLayoutDrawerContent =
  DrawerComponent as any;

CustomLayoutDrawerContent.Title = DrawerTitle as any;
CustomLayoutDrawerContent.Content = Content;
CustomLayoutDrawerContent.Divider = Divider;
CustomLayoutDrawerContent.Separator = Separator;
CustomLayoutDrawerContent.Footer = Footer;
CustomLayoutDrawerContent.TitleHelpText = HelpText;

export { CustomLayoutDrawerContent };
export const DefaultCustomLayoutDrawerProps: DrawerProps = {
  placement: 'right',
  closable: true,
  closeIcon: <CloseIcon showTooltip={false} />,
  mask: true,
  destroyOnClose: true,
};
export type DrawerBreadcrumb = {
  title: ReactNode;
  link?: string;
};
interface DrawerBreadCrumbsProps {
  breadcrumbs: DrawerBreadcrumb[];
}
export const DrawerBreadCrumbs: FC<DrawerBreadCrumbsProps> = ({
  breadcrumbs,
}) => {
  return (
    <>
      {breadcrumbs.map(({ link, title }, index) => (
        <Breadcrumb.Item key={index}>
          {link == null ? (
            title
          ) : link === '' ? (
            <a>{title}</a>
          ) : (
            <Link to={link}>{title}</Link>
          )}
        </Breadcrumb.Item>
      ))}
      <Breadcrumb.Item>
        <></>
      </Breadcrumb.Item>
    </>
  );
};
